import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Upload } from 'lucide-react';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { Button, Sheet, SheetBody, SheetFooter, SheetHeader } from '@/features/common/ui';
import { formatBytes } from '@fint/validation';
import { matchOriginal, originallyLine, type OriginalMatchBy } from './imported';

/**
 * Attach the originals, in bulk, from the old system's export folder.
 *
 * ── WHY THIS EXISTS ──
 * An imported invoice is meant to carry the PDF it was recreated from as its
 * SOURCE. The import tool takes that PDF in the same call, and an AI agent
 * working from a chat window cannot send it - see `matchOriginal` in
 * imported.ts - so the first migration left sixteen invoices with the words
 * "originally INV-000023 in Zoho" and nothing to open. Uploading them one at
 * a time from each invoice page is sixteen trips; this is one: pick the
 * folder's files, read the number off each filename, show which invoice each
 * one is going to, fix the ones it could not place, press once.
 *
 * ── WHAT IT WILL NOT DO ──
 * It will not put a second source on an invoice that has one. The source is
 * the document the invoice IS, and two of them is a question, not an answer;
 * such a file is listed as skipped, and the invoice page is where a source
 * gets replaced, deliberately. Nor does it guess when a filename names two
 * invoices or none: the row waits for the person to choose, and the button
 * counts only the rows that have somewhere to go.
 *
 * Uploads run one after another rather than all at once, for the reason the
 * receipts strip gives: a queue of sixteen that fails on the ninth should
 * say so on the ninth row, with the eight before it done and the seven after
 * it still to do.
 */

interface InvoiceForMatching {
  id: string;
  number: string;
  customerName: string;
  externalNumber?: string | null;
  sourceSystem?: string | null;
  sourceFile?: { id: string } | null;
}

type RowState = 'ready' | 'choose' | 'has-source' | 'uploading' | 'done' | 'failed';

interface Row {
  key: string;
  file: File;
  invoiceId: string | null;
  by: OriginalMatchBy;
  state: RowState;
  error?: string;
}

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/gif';

/** Every invoice in the book, page by page - a small book is one request; a large one is still bounded. */
async function everyInvoice(): Promise<InvoiceForMatching[]> {
  const all: InvoiceForMatching[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.get<{ items: InvoiceForMatching[]; nextCursor?: string }>(`/invoices${qs({ limit: 200, cursor })}`);
    all.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor && all.length < 4000);
  return all;
}

/** "INV-00011 · originally INV-000023 in Zoho · Smarttech" - the option a person picks from. */
function optionLabel(invoice: InvoiceForMatching): string {
  const originally = originallyLine(invoice.externalNumber, invoice.sourceSystem, invoice.number);
  return [invoice.number, originally, invoice.customerName].filter(Boolean).join(' · ');
}

/** Ready when it has an invoice without a source; otherwise waiting on the person, or skipped. */
function stateOf(invoiceId: string | null, invoices: InvoiceForMatching[]): RowState {
  if (!invoiceId) return 'choose';
  return invoices.find((i) => i.id === invoiceId)?.sourceFile ? 'has-source' : 'ready';
}

export function AttachOriginalsSheet({ onClose }: { onClose: () => void }) {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);

  const invoices = useQuery({
    // The same prefix as the list, so a confirm elsewhere refreshes this too.
    queryKey: ['invoices', org?.id, 'for-originals'],
    queryFn: everyInvoice,
    enabled: !!org?.id,
  });
  const candidates = useMemo(() => invoices.data ?? [], [invoices.data]);

  // Files picked before the invoices loaded are matched once they have.
  useEffect(() => {
    if (!candidates.length) return;
    setRows((current) =>
      current.map((row) => {
        if (row.state !== 'choose' || row.by !== 'none' || row.invoiceId) return row;
        const match = matchOriginal(row.file.name, candidates);
        return { ...row, ...match, state: stateOf(match.invoiceId, candidates) };
      }),
    );
  }, [candidates]);

  const add = (files: FileList | File[] | null) => {
    if (!files) return;
    const next: Row[] = Array.from(files).map((file) => {
      const match = candidates.length ? matchOriginal(file.name, candidates) : { invoiceId: null, by: 'none' as const };
      return {
        key: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        ...match,
        state: stateOf(match.invoiceId, candidates),
      };
    });
    setRows((current) => {
      const seen = new Set(current.map((row) => row.key));
      return [...current, ...next.filter((row) => !seen.has(row.key))];
    });
  };

  const choose = (key: string, invoiceId: string) => {
    setRows((current) =>
      current.map((row) =>
        row.key === key
          ? { ...row, invoiceId: invoiceId || null, by: invoiceId ? 'number' : 'none', state: stateOf(invoiceId || null, candidates), error: undefined }
          : row,
      ),
    );
  };

  const remove = (key: string) => setRows((current) => current.filter((row) => row.key !== key));

  const pending = rows.filter((row) => row.state === 'ready' && row.invoiceId);
  const done = rows.filter((row) => row.state === 'done').length;

  const attach = async () => {
    setRunning(true);
    // An invoice can take one source: the second file aimed at the same one
    // in this batch waits, so a folder with two scans of an invoice does not
    // put both on it.
    const taken = new Set<string>();
    for (const row of pending) {
      if (!row.invoiceId || taken.has(row.invoiceId)) {
        setRows((current) => current.map((r) => (r.key === row.key ? { ...r, state: 'has-source' } : r)));
        continue;
      }
      taken.add(row.invoiceId);
      setRows((current) => current.map((r) => (r.key === row.key ? { ...r, state: 'uploading' } : r)));
      try {
        await api.upload(`/files?ownerType=invoice&ownerId=${row.invoiceId}&role=source`, row.file);
        setRows((current) => current.map((r) => (r.key === row.key ? { ...r, state: 'done' } : r)));
      } catch (e) {
        const message = e instanceof ApiError ? e.message : 'The upload did not finish';
        setRows((current) => current.map((r) => (r.key === row.key ? { ...r, state: 'failed', error: message } : r)));
      }
    }
    setRunning(false);
    void queryClient.invalidateQueries({ queryKey: ['invoices', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['attachments', org?.id] });
  };

  const titleId = 'attach-originals-title';

  return (
    <Sheet labelledBy={titleId} onClose={running ? () => undefined : onClose} wide testId="attach-originals">
      <SheetHeader title="Attach originals" titleId={titleId} onClose={running ? () => undefined : onClose} />
      <SheetBody>
        <p className="text-sm text-muted-foreground">
          Pick the PDFs from the old system&rsquo;s export. Each one is matched to its invoice by the number in the
          filename &mdash; <span className="font-mono text-xs">Invoice_INV-000023.pdf</span> goes to the invoice that was
          originally INV-000023 &mdash; and attached as its source.
        </p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            add(e.dataTransfer.files);
          }}
          className={[
            'flex flex-col items-center gap-2 rounded-md border border-dashed p-4 text-center transition-colors',
            dragging ? 'border-primary bg-primary/5' : 'border-border',
          ].join(' ')}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            className="sr-only"
            accept={ACCEPT}
            aria-label="Choose the original files"
            data-testid="attach-originals-input"
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={running}>
            <Upload className="size-3.5" aria-hidden />
            Choose files
          </Button>
          <p className="text-xs text-muted-foreground">or drop the whole folder&rsquo;s files here. PDF or image, up to the usual size.</p>
        </div>

        {invoices.isLoading ? <p className="text-xs text-muted-foreground">Loading the invoices to match against&hellip;</p> : null}

        {rows.length ? (
          <ul className="divide-y divide-border rounded-md border border-border" data-testid="attach-originals-rows">
            {rows.map((row) => (
              <li key={row.key} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center" data-state={row.state} data-testid="attach-originals-row">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium" title={row.file.name}>
                      {row.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(row.file.size)} · <RowStatus row={row} />
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:w-[46%]">
                  {row.state === 'done' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="size-4" aria-hidden />
                      Attached
                    </span>
                  ) : (
                    <>
                      <select
                        aria-label={`Invoice for ${row.file.name}`}
                        value={row.invoiceId ?? ''}
                        onChange={(e) => choose(row.key, e.target.value)}
                        disabled={running || row.state === 'uploading'}
                        className="min-h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring touch:min-h-11 touch:text-base"
                      >
                        <option value="">Choose the invoice…</option>
                        {candidates.map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            {optionLabel(invoice)}
                          </option>
                        ))}
                      </select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => remove(row.key)} disabled={running} aria-label={`Remove ${row.file.name}`}>
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" onClick={() => { attach().catch(() => undefined); }} disabled={running || pending.length === 0} loading={running} data-testid="attach-originals-go">
          {pending.length === 1 ? 'Attach 1 original' : `Attach ${pending.length} originals`}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
          {done ? 'Done' : 'Cancel'}
        </Button>
        {done ? (
          <p className="ml-auto text-xs text-muted-foreground" role="status">
            {done} attached
          </p>
        ) : null}
      </SheetFooter>
    </Sheet>
  );
}

/** What the matcher decided, in words a person can act on. */
function RowStatus({ row }: { row: Row }) {
  switch (row.state) {
    case 'ready':
      return <>{row.by === 'external' ? 'matched by its old number' : 'matched by its number'}</>;
    case 'choose':
      return <>{row.by === 'ambiguous' ? 'names more than one invoice - choose' : 'no invoice number in the filename - choose'}</>;
    case 'has-source':
      return <>that invoice already has a source - skipped</>;
    case 'uploading':
      return <>uploading…</>;
    case 'done':
      return <>attached as the source</>;
    case 'failed':
      return <span className="text-destructive">{row.error ?? 'failed'}</span>;
  }
}
